// import { DragIndicatorOutlined } from "@mui/icons-material"; // used by the removed drag handle
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import {
  Box,
  Chip,
  Grid,
  InputAdornment,
  Popper,
  PopperProps,
  Typography,
} from "@mui/material";
import { GridSortItem } from "@mui/x-data-grid";
import { FormikProps } from "formik";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { AsyncSelectValue } from "../../../../../../components/AsyncSelect";
import AsyncSelectScroll from "../../../../../../components/AsyncSelectScroll";
import CustomSelectScroll from "../../../../../../components/CustomSelectScroll";
import { FlexCenterWrapper } from "../../../../../../components/DefaultNoDataWrapper";
import ErrorHelper from "../../../../../../components/ErrorWrapper";
import Flex from "../../../../../../components/Flex";
import Input from "../../../../../../components/Input";
import MaskInput from "../../../../../../components/MaskInput";
import OutlineCard from "../../../../../../components/OutlineCard";
import Text from "../../../../../../components/Text";
import DeleteIcon from "../../../../../../components/app/DeleteIcon";
import { ADD_SUPER_BILL_PROCEDURE_INITIAL_STATE } from "../../../../../../constants/provider/super-bill/add-super-bill/add-super-bill";
import useDebouncedCallback from "../../../../../../hooks/use-debounced-callback";
import { useSnackbar } from "../../../../../../hooks/useSnackbar";
import { useGetAmountFromFeeScheduleByPayer } from "../../../../../../queries/billings/use-get-amount-from-fee-schedule-by-type";
import {
  // GetSubTotalForPatientProcedureCodeProps,
  SubTotalProcedureCodePayload,
  useCheckSubTotalForPatientProcedureCode,
} from "../../../../../../queries/billings/use-get-sub-total-for-patient-by-procedure-cd";
import useGetCptHcpcsCode from "../../../../../../queries/catalog/use-get-cpt-hcp-medical-code";
import useGetModifiers from "../../../../../../queries/modifiers/use-get-modifiers";
import {
  GetFeeScheduleAmountByPayerAndTypeFeeScheduleTypeEnum,
  MedicalCode,
} from "../../../../../../services";
import { Identified } from "@/types/sdk-types";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  SECONDARY_TEXT_COLOR,
  getXtenantId,
  toTwoDecimals,
} from "../../../../../../utils/utils";
import CardView from "../../../patient/patient-list/patient-chart-new/assessment/summary/components/CardView";
import DiagnosisPointers from "../../common-components/DiagnosisPointers";
import {
  CHARGES_FIELD_SX,
  CPT_FIELD_GROUP_SPACER_LABEL,
  PROCEDURE_CODE_SECTION_ID,
  PROCEDURE_ROW_ERROR_KEYS,
  ProcedureRowErrorKey,
} from "../claims-constants";
import { formatChargeAmount, getProcedureRowError } from "../claims-utils";
import { Option } from "../../../../../../constants/common";
import {
  FeeScheduleAmount,
  ProcedureCodeFormRow,
  ProcedureCodeGridRow,
  ProcedureRowField,
} from "../../billing-types";

interface ProcedureCodeGridForClaimProps {
  procedureCodeFormik: FormikProps<ProcedureCodeFormRow[]>;
  readOnly?: string;
  renderingProviderUuid?: string;
  payerId?: string;
  procedureCodeTotal: number;
  diagnosisCodesLength: number;
}

// Currency symbol shown inside the monetary inputs so the claim form matches
// how amounts are rendered everywhere else in billing.
const dollarAdornment = {
  startAdornment: <InputAdornment position="start">$</InputAdornment>,
};

const ProcedureCodeGridForClaim = ({
  diagnosisCodesLength,
  procedureCodeFormik,
  readOnly,
  renderingProviderUuid,
  payerId,
  procedureCodeTotal,
}: ProcedureCodeGridForClaimProps) => {
  const showSnackbar = useSnackbar();

  const [pagination, setPagination] = useState<DGT_Pagination>({
    page: 0,
    pageSize: 100,
  });

  const [gridSorting] = useState<GridSortItem[]>([]);
  const [gridData, setGridData] = useState<ProcedureCodeGridRow[]>([]);
  const [searchKey, setSearchKey] = useState<string>("");
  const [input, setInput] = useState<string | null>("");

  const [modifierSearchKey, setModifierSearchKey] = useState<string>("");
  const [modifierPagination, setModifierPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);

  const { data: modifierData, isFetching: isFetchingModifiers } =
    useGetModifiers({
      searchString: modifierSearchKey,
      page: modifierPagination.page,
      size: modifierPagination.pageSize,
      xTENANTID: getXtenantId(),
    });

  const { data, isFetching: codeFetching } = useGetCptHcpcsCode({
    gridSorting,
    pagination,
    searchKey,
    active: true,
    archive: false,
  });

  // The picked CPT can come from a page no longer in `data.content` once the
  // list has scrolled, so keep every loaded code by uuid to resolve it.
  const codeByUuid = useRef<Record<string, Identified<MedicalCode>>>({});
  useEffect(() => {
    (data?.data?.content ?? []).forEach((it) => {
      if (it?.uuid) codeByUuid.current[it.uuid] = it;
    });
  }, [data?.data?.content]);

  // interface ParamCalculateSub extends GetSubTotalForPatientProcedureCodeProps {
  //   idx: number;
  // }
  const { mutateAsync: checkSubTotal } =
    useCheckSubTotalForPatientProcedureCode();

  // Plain (non-debounced) sub-total calc for a single row. Kept separate from
  // the debounced wrapper so it can be invoked once per row when several codes
  // are auto-populated at the same time (a shared debounce timer would collapse
  // the concurrent calls into a single one).
  const calculateSubTotal = ({
    patientProcedureCode,
    idx,
  }: {
    patientProcedureCode: SubTotalProcedureCodePayload;
    idx: number;
  }) => {
    if (!patientProcedureCode) return;
    return checkSubTotal({
      patientProcedureCode: patientProcedureCode,
      renderingProviderUuid: renderingProviderUuid ?? undefined,
    }).then((data) => {
      procedureCodeFormik.setFieldValue(`[${idx}].total`, data.data.data);
    });
  };

  const debouncedCalculateSubTotal = useDebouncedCallback(
    calculateSubTotal,
    500
  );
  const { mutateAsync: getAmountFromFeeSchedule } =
    useGetAmountFromFeeScheduleByPayer();

  // Plain (non-debounced) payer-specific fee schedule lookup + prefill for a
  // single row index. Debouncing uses one shared timer, so calling the debounced
  // version in a loop for N codes would clear all but the last timer and only
  // prefill one row. Calling this directly runs one independent lookup per code,
  // each writing to its own index.
  const applyFeeScheduleForCode = ({
    procedureCode,
    idx,
  }: {
    procedureCode: string;
    idx: number;
  }) => {
    if (!procedureCode || !payerId) return;
    return getAmountFromFeeSchedule({
      payerId,
      codeId: procedureCode,
      feeScheduleType:
        GetFeeScheduleAmountByPayerAndTypeFeeScheduleTypeEnum.PayerSpecific,
    }).then((data) => {
      const result = data?.data?.data as FeeScheduleAmount | undefined;
      const isModifierAvail = Object.keys(
        procedureCodeFormik?.values?.[idx]?.modifiers ?? {}
      )?.length;

      if (result?.modifier && !isModifierAvail) {
        procedureCodeFormik.setFieldValue(
          `[${idx}].modifiers`,
          Object.entries(result.modifier).map(([uuid, modDesc]) => {
            return { value: uuid, label: modDesc };
          })
        );
      }
      if (result?.amount) {
        procedureCodeFormik.setFieldValue(
          `[${idx}].charges`,
          formatChargeAmount(result.amount)
        );

        // recompute this row's sub-total so the auto-populated charge is
        // reflected in the claim total (setFieldValue does not fire the input
        // onChange handlers). Uses the non-debounced calc so every row is
        // computed when several codes are prefilled together.
        const row = procedureCodeFormik.values?.[idx];
        calculateSubTotal({
          patientProcedureCode: {
            procedureCode: row?.procedureCode,
            quantity: row?.quantity ?? 1,
            charges: result.amount,
            discount: row?.discount,
            discountType: row?.discountType,
            patientProcedureCodeType: "CLAIM",
          },
          idx,
        });
      }
      if (result?.fixed) {
        procedureCodeFormik.setFieldValue(`[${idx}].disableCharges`, true);
      }
    });
  };

  const debouncedGetAmountFromFeeSchedule = useDebouncedCallback(
    applyFeeScheduleForCode,
    0
  );

  // Track CPT codes whose fee schedule has already been looked up so prefilled
  // codes are only auto-populated once.
  const feeScheduleProcessedRef = useRef<Set<string>>(new Set());

  // Always point at the latest formik instance so async callbacks read/write
  // the freshest values (avoids stale-snapshot writes clobbering each other).
  const formikRef = useRef(procedureCodeFormik);
  formikRef.current = procedureCodeFormik;

  // Auto-populate quantity (default 1) and the payer-specific fee schedule
  // values (charges, modifiers, fixed) for CPT codes prefilled from the backend
  // (e.g. from the encounter). All pending codes are looked up in parallel and
  // applied in a SINGLE setValues so each row keeps its own data and no
  // resolving lookup overwrites another row's prefill. Manual additions are
  // handled in the CPT onChange (which marks the code as processed here).
  useEffect(() => {
    if (!payerId || !procedureCodeFormik.values?.length) return;

    const pending: { idx: number; codeUuid: string; hasCharges: boolean }[] =
      [];
    procedureCodeFormik.values.forEach((item, idx) => {
      const codeUuid = Object.keys(item?.procedureCode ?? {})[0];
      if (!codeUuid || feeScheduleProcessedRef.current.has(codeUuid)) return;
      feeScheduleProcessedRef.current.add(codeUuid);
      pending.push({ idx, codeUuid, hasCharges: !!item?.charges });
    });
    if (!pending.length) return;

    (async () => {
      // 1) fetch the payer-specific fee schedule for every pending code.
      // Rows that already have charges (editing an existing claim) are kept.
      const feeResults = await Promise.all(
        pending.map(({ idx, codeUuid, hasCharges }) => {
          if (hasCharges)
            return Promise.resolve({
              idx,
              res: null as FeeScheduleAmount | null,
            });
          return getAmountFromFeeSchedule({
            payerId,
            codeId: codeUuid,
            feeScheduleType:
              GetFeeScheduleAmountByPayerAndTypeFeeScheduleTypeEnum.PayerSpecific,
          })
            .then((data) => ({
              idx,
              res: (data?.data?.data as FeeScheduleAmount | undefined) ?? null,
            }))
            .catch(() => ({ idx, res: null as FeeScheduleAmount | null }));
        })
      );

      // 2) build the next values from the latest committed state, defaulting
      // quantity and applying each fee schedule to its OWN index only.
      const next: ProcedureCodeFormRow[] = formikRef.current.values.map(
        (r) => ({ ...r })
      );
      pending.forEach(({ idx }) => {
        if (next[idx] && !next[idx].quantity) next[idx].quantity = 1;
      });
      feeResults.forEach(({ idx, res }) => {
        if (!res || !next[idx]) return;
        const isModifierAvail = Object.keys(next[idx].modifiers ?? {}).length;
        if (res.modifier && !isModifierAvail) {
          next[idx].modifiers = Object.entries(res.modifier).map(
            ([uuid, modDesc]) => ({ value: uuid, label: modDesc })
          );
        }
        if (res.amount)
          next[idx].charges = formatChargeAmount(res.amount) ?? undefined;
        if (res.fixed) next[idx].disableCharges = true;
      });

      // 3) compute the sub-total for every row that received a charge, again in
      // parallel, and merge the results into the same array.
      const totals = await Promise.all(
        feeResults
          .filter(
            (feeResult): feeResult is { idx: number; res: FeeScheduleAmount } =>
              !!feeResult.res?.amount
          )
          .map(({ idx, res }) => {
            const row = next[idx];
            return checkSubTotal({
              patientProcedureCode: {
                procedureCode: row?.procedureCode,
                quantity: row?.quantity ?? 1,
                charges: res.amount,
                discount: row?.discount,
                discountType: row?.discountType,
                patientProcedureCodeType: "CLAIM",
              },
              renderingProviderUuid: renderingProviderUuid ?? undefined,
            })
              .then((data) => ({
                idx,
                total: data?.data?.data as number | undefined,
              }))
              .catch(() => ({ idx, total: undefined }));
          })
      );
      totals.forEach(({ idx, total }) => {
        if (total !== undefined && next[idx]) next[idx].total = total;
      });

      // 4) apply everything atomically so no index overwrites another.
      formikRef.current.setValues(next);
    })();
  }, [procedureCodeFormik.values, payerId]);

  const getKey = (k: ProcedureRowField, ind: number) => `${ind}.${k}`;

  const getRowError = (ind: number, key: ProcedureRowErrorKey) =>
    getProcedureRowError(procedureCodeFormik, ind, key);

  const selectHandleChange = (
    k: ProcedureRowField,
    v: string | AsyncSelectValue | null,
    ind: number
  ) => {
    procedureCodeFormik.setFieldValue(getKey(k, ind), v);
  };

  // console.log("gridData", gridData);
  // console.log("procedure code formik ", procedureCodeFormik.values);

  // console.log("formiks", formiks.values);
  useEffect(() => {
    setGridData(
      procedureCodeFormik.values.map((itm) => {
        return {
          uuid: itm?.uuid,
          codeVersion: "",
          type: itm?.type,
          code: Object.values(itm?.procedureCode ?? {})[0]?.split(" - ")[0],
          description: Object.values(itm?.procedureCode ?? {})[0]
            ?.split(" - ")
            ?.slice(1)
            ?.join(" - "),
          source: "",
          active: itm.active,
          archive: itm.archive,
          errorMessage: "",
          procedureCode: Object.keys(itm?.procedureCode ?? {})[0],
        };
      })
    );
  }, [procedureCodeFormik.values]);

  // below flow is to view only data
  // useEffect(() => {
  //   if (procedureCodes) {
  //     console.log("Procedure Codes ", procedureCodes);
  //   }
  // }, [procedureCodes]);

  return (
    <Box id={PROCEDURE_CODE_SECTION_ID}>
      <CardView
        title="CPT Code"
        titleIcon={
          <ListAltOutlinedIcon sx={{ color: "orange" }} fontSize="small" />
        }
        endAction={
          !readOnly && (
            <Box sx={{ paddingX: "5px" }} width={"30.5vw"}>
              <CustomSelectScroll
                key={`cpt-claim-select-${gridData?.length ?? 0}`}
                loading={codeFetching}
                pagination={pagination}
                setPagination={setPagination}
                totalElements={data?.data?.page?.totalElements}
                onSearch={(_, val) => {
                  setSearchKey(val);
                  setPagination((p) => ({ ...p, page: 0 }));
                }}
                options={data?.data?.content?.reduce((acc: Option[], it) => {
                  if (
                    !gridData.some((item) => item.procedureCode === it.uuid)
                  ) {
                    acc.push({
                      label: `${it.code ? it.code : "--"} - ${it.description}`,
                      value: it.uuid,
                    });
                  }
                  return acc;
                }, [])}
                onChange={(v) => {
                  const curOption =
                    codeByUuid.current[v as string] ??
                    data?.data?.content?.find((it) => it.uuid === v);

                  setInput(v);

                  if (!gridData.some((item) => item.procedureCode === v)) {
                    if (curOption) {
                      setInput("");
                      setSearchKey("");
                      setPagination({ page: 0, pageSize: 100 });
                      setGridData((prevItems) => [
                        ...prevItems,
                        {
                          uuid: undefined,
                          codeVersion: "",
                          type: curOption.type,
                          code: curOption.code,
                          description: curOption.description,
                          source: "",
                          active: true,
                          archive: false,
                          errorMessage: "",
                          procedureCode: curOption.uuid,
                        },
                      ]);

                      procedureCodeFormik.setValues([
                        ...(procedureCodeFormik.values ?? {}),
                        ADD_SUPER_BILL_PROCEDURE_INITIAL_STATE[0],
                      ]);

                      procedureCodeFormik.setFieldValue(
                        getKey(
                          "procedureCode",
                          procedureCodeFormik.values.length
                        ),
                        {
                          [curOption?.uuid]: `${curOption.code ?? "--"} - ${
                            curOption.description
                          }`,
                        }
                      );

                      procedureCodeFormik.setFieldValue(
                        `[${procedureCodeFormik.values.length}].type`,
                        curOption.type
                      );

                      // mark as processed so the prefill effect does not run a
                      // duplicate lookup for this manually added code
                      feeScheduleProcessedRef.current.add(curOption.uuid);

                      // to prefill the modifier data right after selection of the claim
                      setTimeout(() => {
                        debouncedGetAmountFromFeeSchedule({
                          procedureCode: curOption.uuid,
                          idx: procedureCodeFormik?.values?.length,
                        });
                      }, 0);
                    } else {
                      setSearchKey("");
                      setInput("");
                      showSnackbar({
                        message: "Procedure Code already exists",
                        type: "error",
                      });
                    }
                  }
                }}
                value={input}
                placeholder="Search & select CPT Code"
              />
            </Box>
          )
        }
      >
        {/* multiple box.  */}
        <Box sx={{ overflowY: "scroll" }} maxHeight={"500px"} height={"100%"}>
          {gridData.map((itm, idx) => (
            <Flex
              key={itm.uuid}
              flexDirection={"column"}
              height={"100%"}
              gap={1}
            >
              <OutlineCard
                style={{
                  height: "30%",
                  width: "100%",
                  borderLeft: "30px solid #d8edf9",
                  position: "relative",
                  overflow: "visible",
                  margin: "10px 0px 10px 0px",
                }}
              >
                {/* The 6-dot handle never moved anything — rows are not
                    reorderable — so it is removed rather than left as a dead
                    affordance. Restore this block if drag-to-reorder is built:

                <Box
                  sx={{
                    position: "absolute",
                    left: "-30px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    backgroundColor: "#d8edf9",
                    padding: "1px",
                    border: "1px solid #d8edf9",
                    zIndex: 2,
                  }}
                >
                  <DragIndicatorOutlined />
                </Box> */}

                {/* details of card  */}
                <Flex
                  height={"30px"}
                  justifyContent={"space-around"}
                  alignItems={"stretch"}
                >
                  <Flex gap={1}>
                    <Text
                      fontWeight={"bold"}
                      variant="h6"
                      fontSize={"14px"}
                      color={"primary.main"}
                      alignSelf={"center"}
                    >
                      {idx + 1}.
                    </Text>
                    <Text
                      fontWeight={"bold"}
                      variant="h6"
                      fontSize={"14px"}
                      color={"primary.main"}
                      alignSelf={"center"}
                    >
                      {itm?.code}
                    </Text>
                  </Flex>
                  <Box width={"80%"}>
                    <Text
                      fontWeight={"bold"}
                      variant="h6"
                      fontSize={"15px"}
                      color={SECONDARY_TEXT_COLOR}
                      alignSelf={"center"}
                      marginLeft={"8px"}
                    >
                      {itm?.description}
                    </Text>
                  </Box>

                  <Box
                    sx={{
                      width: "10%",
                      display: "flex",
                      flexDirection: "row-reverse",
                    }}
                  >
                    {/* <ButtonBase
                      disabled={!!readOnly}
                      onClick={() => {
                        setGridData(gridData.toSpliced(idx, 1));
                        procedureCodeFormik.setValues(
                          procedureCodeFormik.values.toSpliced(idx, 1)
                        );
                      }}
                    > */}
                    <Typography fontSize={"18px"} fontWeight={"500"}>
                      <DeleteIcon
                        disabled={!!readOnly}
                        onClick={() => {
                          setGridData(gridData.toSpliced(idx, 1));
                          procedureCodeFormik.setValues(
                            procedureCodeFormik.values.toSpliced(idx, 1)
                          );
                        }}
                      />
                      {/* <DeleteOutlineIcon
                          color={readOnly ? "disabled" : "error"}
                          sx={{
                            cursor: "pointer",
                            color: readOnly ? "disabled" : "error",
                          }}
                        /> */}
                    </Typography>
                    {/* </ButtonBase> */}
                    <Box
                      sx={{
                        borderRight: "1px solid lightgray",
                        paddingRight: "10px",
                        marginRight: "10px",
                      }}
                    >
                      <Chip
                        size="small"
                        color="primary"
                        label={<Text fontSize={"12px"}>{itm?.type}</Text>}
                        sx={{ borderRadius: "5px" }}
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                </Flex>

                {/* modifiers  diagnosis pointers and other details. */}
                <Grid container paddingLeft={"10px"} spacing={4}>
                  <Grid item xs={4}>
                    <ErrorHelper
                      label="Modifiers"
                      error={getRowError(
                        idx,
                        PROCEDURE_ROW_ERROR_KEYS.MODIFIERS
                      )}
                    >
                      <Flex gap={1}>
                        {Array.from({ length: 4 }).map((_, ind) => (
                          <Box key={ind} width={"25%"}>
                            <AsyncSelectScroll
                              disabled={!!readOnly}
                              loading={isFetchingModifiers}
                              pagination={modifierPagination}
                              setPagination={setModifierPagination}
                              totalElements={
                                modifierData?.data?.page?.totalElements
                              }
                              onSearch={(val) => {
                                setModifierSearchKey(val);
                                setModifierPagination((p) => ({
                                  ...p,
                                  page: 0,
                                }));
                              }}
                              options={modifierData?.data?.content?.map(
                                (it) => ({
                                  value: it.uuid,
                                  label: `${it.code}-${it.description ?? ""}`,
                                })
                              )}
                              value={
                                procedureCodeFormik.values?.[idx]?.modifiers?.[
                                  ind
                                ]
                              }
                              onChange={(value: AsyncSelectValue | null) => {
                                // Prevent the same modifier being picked in more
                                // than one of the 4 slots for a single CPT.
                                const selectedId = value?.value ?? value;
                                if (
                                  selectedId &&
                                  (
                                    procedureCodeFormik.values?.[idx]
                                      ?.modifiers ?? []
                                  ).some(
                                    (m, i) =>
                                      i !== ind &&
                                      (m?.value ?? m) === selectedId
                                  )
                                ) {
                                  showSnackbar({
                                    message:
                                      "This modifier is already selected",
                                    type: "error",
                                  });
                                  return;
                                }
                                selectHandleChange(
                                  `modifiers.[${ind}]`,
                                  value,
                                  idx
                                );
                              }}
                              CustomPopperComponent={(props: PopperProps) => (
                                <Popper
                                  {...props}
                                  style={{
                                    width: 500,
                                  }}
                                  placement="bottom-start"
                                />
                              )}
                            />
                          </Box>
                        ))}
                      </Flex>
                    </ErrorHelper>
                  </Grid>

                  <DiagnosisPointers
                    diagnosisCodesLength={diagnosisCodesLength}
                    viewBill={!readOnly}
                    procedureCodeFormik={procedureCodeFormik}
                    selectHandleChange={selectHandleChange}
                    idx={idx}
                    required
                    error={getRowError(
                      idx,
                      PROCEDURE_ROW_ERROR_KEYS.DIAGNOSIS_POINTER
                    )}
                  />

                  <Grid item xs={4}>
                    {/* The columns to the left open with a label, so this group
                        carries a spacer one: the inputs then sit on the same
                        line as the selects and each error message grows
                        downwards instead of pushing its own input up. */}
                    <ErrorHelper label={CPT_FIELD_GROUP_SPACER_LABEL}>
                      <Flex alignItems={"flex-start"} gap={2}>
                        <MaskInput
                          mask={"99999"}
                          disabled={!!readOnly}
                          label="Quantity"
                          required
                          name={getKey("quantity", idx)}
                          error={getRowError(
                            idx,
                            PROCEDURE_ROW_ERROR_KEYS.QUANTITY
                          )}
                          placeholder="Quantity"
                          value={procedureCodeFormik.values?.[idx]?.quantity}
                          onChange={(e) => {
                            selectHandleChange("quantity", e.target.value, idx);

                            debouncedCalculateSubTotal({
                              patientProcedureCode: {
                                procedureCode: {
                                  [`${itm?.procedureCode}`]: `${itm.description}`,
                                },
                                quantity: e.target.value,
                                charges:
                                  procedureCodeFormik.values?.[idx]?.charges,
                                patientProcedureCodeType: "CLAIM",
                              },
                              idx: idx,
                            });

                            debouncedGetAmountFromFeeSchedule({
                              procedureCode: `${itm?.procedureCode}`,
                              idx: idx,
                            });
                          }}
                        />
                        <Input
                          label="Charges"
                          required
                          error={getRowError(
                            idx,
                            PROCEDURE_ROW_ERROR_KEYS.CHARGES
                          )}
                          textFieldProps={{
                            type: "number",
                            name: getKey("charges", idx),
                            placeholder: "Charges",
                            disabled:
                              !!readOnly ||
                              procedureCodeFormik.values?.[idx]?.disableCharges,
                            value: procedureCodeFormik.values?.[idx]?.charges,
                            onChange: (e: ChangeEvent<HTMLInputElement>) => {
                              const value = e.target.value;
                              if (
                                value === "" ||
                                /^\d*\.?\d{0,2}$/.test(value)
                              ) {
                                selectHandleChange("charges", value, idx);
                                debouncedCalculateSubTotal({
                                  patientProcedureCode: {
                                    procedureCode: {
                                      [`${itm?.procedureCode}`]: `${itm.description}`,
                                    },
                                    quantity:
                                      procedureCodeFormik.values?.[idx]
                                        ?.quantity,
                                    charges: value,
                                    patientProcedureCodeType: "CLAIM",
                                  },
                                  idx: idx,
                                });
                              }
                            },
                            onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                              let value = e.target.value;
                              if (value) {
                                value = parseFloat(value).toFixed(2);
                                selectHandleChange("charges", value, idx);
                              }
                            },
                            InputProps: dollarAdornment,
                            inputProps: {
                              style: {
                                MozAppearance: "textfield",
                              },
                              inputMode: "numeric",
                              pattern: "^\\d*\\.?$",
                            },
                            sx: CHARGES_FIELD_SX,
                          }}
                        />
                        <Input
                          textFieldProps={{
                            disabled: true,
                            placeholder: "Total",
                            // Charges is rounded to 2 decimals on blur, so Total
                            // is displayed the same way instead of echoing the
                            // raw computed value (200 vs 200.00).
                            value: toTwoDecimals(
                              procedureCodeFormik.values?.[idx]?.total
                            ),
                            InputProps: dollarAdornment,
                          }}
                          label="Total"
                        />
                      </Flex>
                    </ErrorHelper>
                  </Grid>
                </Grid>
              </OutlineCard>
            </Flex>
          ))}
        </Box>

        {!procedureCodeFormik.values?.length && (
          <FlexCenterWrapper>No Records Available</FlexCenterWrapper>
        )}
        {/* Procedure Total */}
        {!procedureCodeTotal.toFixed(2) && (
          <Flex justifyContent={"end"} alignItems={"center"} mr={2} pt={1}>
            <Text fontWeight="bold">Procedural Charges Total: &nbsp;</Text>

            <Chip
              label={
                <Text fontWeight="bold" fontSize={"16px"}>
                  ${procedureCodeTotal.toFixed(2)}
                </Text>
              }
              size="small"
              color="primary"
              variant="outlined"
              sx={{ borderRadius: "5px" }}
            />
          </Flex>
        )}
      </CardView>
    </Box>
  );
};

export default ProcedureCodeGridForClaim;
